import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Send, Loader2, Paperclip, X, Image as ImageIcon, Check, Camera as CameraIcon, PenLine } from "lucide-react";
import { toast } from "sonner";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import DuplicateDialog from "@/components/DuplicateDialog";
import PhotoEditor from "@/components/photo-editor/PhotoEditor";
import { enterSubmits, isTouchDevice } from "@/lib/keys";

const URL_RE = /^(https?:\/\/)[^\s]+$/i;
const MAX_BYTES = 50 * 1024 * 1024; // 50MB
const isNative = () => Capacitor.isNativePlatform();

/** Turn a Capacitor photo (file:// or capacitor:// uri) into a real File. */
async function photoToFile(photo, idx = 0) {
  const res = await fetch(photo.webPath || photo.path);
  const blob = await res.blob();
  const ext = (photo.format || "jpg").replace("jpeg", "jpg");
  return new File([blob], `photo-${Date.now()}-${idx}.${ext}`, {
    type: blob.type || `image/${photo.format || "jpeg"}`,
  });
}

function resetTextareaHeight(el) {
  if (!el) return;
  el.style.height = "auto";
}

const Composer = forwardRef(function Composer({ onSend, onUpload, findDuplicate, onFindExisting, onDrawLine }, ref) {
  const [dup, setDup] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [editPhoto, setEditPhoto] = useState(null);
  // index -> { pct, done } while a send is in flight
  const [uploads, setUploads] = useState({});
  const fileInputRef = useRef(null);
  const textRef = useRef(null);
  // Track IME composition (Greek/CJK/Arabic keyboards, autocorrect on Android)
  // so we don't submit mid-composition and don't fight the IME with re-renders.
  const composingRef = useRef(false);

  // Attach files without uploading them — the user can type a caption first and
  // send everything together. Used by the paperclip and by drag & drop.
  const attachFiles = (files) => {
    const accepted = Array.from(files || []).filter((f) => {
      if (f.size > MAX_BYTES) {
        toast.error(`"${f.name}" is too large (max 50 MB)`);
        return false;
      }
      return true;
    });
    if (!accepted.length) return;
    setPendingFiles((prev) => [...prev, ...accepted]);
    textRef.current?.focus();
  };

  useImperativeHandle(ref, () => ({ attachFiles }));

  const submit = async (e, force = false) => {
    e?.preventDefault?.();
    const trimmed = text.trim();
    if (busy) return;

    if (!force && findDuplicate && (pendingFiles.length || trimmed)) {
      const url = pendingFiles.length ? null : (trimmed.match(/(https?:\/\/[^\s]+)/i) || [])[1];
      if (pendingFiles.length || url) {
        setBusy(true);
        let found = null;
        try { found = await findDuplicate({ url, files: pendingFiles }); } catch { /* send anyway */ }
        setBusy(false);
        if (found) { setDup({ ...found, kind: pendingFiles.length ? "file" : "link" }); return; }
      }
    }

    if (pendingFiles.length) {
      setBusy(true);
      // Several files sent in one go stay one note: they share a group id.
      const groupId =
        pendingFiles.length > 1
          ? `grp${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
          : "";
      try {
        // The caption rides with the first attachment; the rest go up bare.
        for (let i = 0; i < pendingFiles.length; i++) {
          setUploads((prev) => ({ ...prev, [i]: { pct: 0 } }));
          await onUpload(
            pendingFiles[i],
            i === 0 ? trimmed : "",
            (pct) => setUploads((prev) => ({ ...prev, [i]: { pct } })),
            groupId,
          );
          setUploads((prev) => ({ ...prev, [i]: { pct: 100, done: true } }));
        }
        setPendingFiles([]);
        setUploads({});
        setText("");
        resetTextareaHeight(textRef.current);
      } catch {
        toast.error("Upload failed");
        setUploads({});
      } finally {
        setBusy(false);
      }
      return;
    }

    if (!trimmed) return;
    setBusy(true);
    try {
      let payload;
      if (URL_RE.test(trimmed)) {
        payload = { type: "link", content: "", url: trimmed };
      } else {
        const urlMatch = trimmed.match(/(https?:\/\/[^\s]*[^\s.,;:!?)"'])/i);
        if (urlMatch) {
          payload = { type: "link", content: trimmed, url: urlMatch[1] };
        } else {
          payload = { type: "note", content: trimmed, url: null };
        }
      }
      await onSend(payload);
      setText("");
      resetTextareaHeight(textRef.current);
    } catch {
      toast.error("Send failed");
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    // Do NOT swallow Enter while the IME is composing (e.g. Greek autocorrect
    // or CJK candidate popup) — that character isn't final yet.
    if (composingRef.current || e.nativeEvent?.isComposing || e.keyCode === 229) return;
    if (enterSubmits(e)) {
      e.preventDefault();
      submit();
    }
  };

  const onPickFile = (e) => {
    // Snapshot the FileList BEFORE clearing the input — resetting .value
    // empties the live FileList and the files would be lost.
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    attachFiles(files);
  };

  const removeAt = (idx) => setPendingFiles((prev) => prev.filter((_, i) => i !== idx));

  // Android: pull straight from the gallery / camera instead of the file browser.
  const pickFromGallery = async () => {
    try {
      const { photos } = await Camera.pickImages({ quality: 85, limit: 10 });
      if (!photos?.length) return;
      attachFiles(await Promise.all(photos.map((p, i) => photoToFile(p, i))));
    } catch (err) {
      if (!/cancel/i.test(err?.message || "")) toast.error("Couldn't open your gallery");
    }
  };

  const takePhoto = async () => {
    try {
      const photo = await Camera.getPhoto({
        quality: 85,
        source: CameraSource.Camera,
        resultType: CameraResultType.Uri,
        saveToGallery: false,
      });
      setEditPhoto(await photoToFile(photo));
    } catch (err) {
      if (!/cancel/i.test(err?.message || "")) toast.error("Couldn't open the camera");
    }
  };

  const sendEditedPhoto = async (file, caption) => {
    await onUpload(file, caption, () => {}, "");
    setEditPhoto(null);
  };

  const native = isNative() || (typeof localStorage !== "undefined" && localStorage.getItem("nl_native_ui") === "1");
  const btn = "h-12 w-12 shrink-0 rounded-full bg-[#1E1E1E] hover:bg-[#27272A] border border-[#27272A] text-[#9CA3AF] hover:text-[#3B82F6] flex items-center justify-center transition-all disabled:opacity-40";
  const penBtn = "h-12 w-12 shrink-0 rounded-full bg-[#1E1E1E] hover:bg-[#27272A] border border-[#27272A] text-[#F87171] hover:text-[#EF4444] flex items-center justify-center transition-all disabled:opacity-40";
  const pillIcon = "h-10 w-9 shrink-0 flex items-center justify-center rounded-full text-[#E5E7EB] active:scale-90 active:bg-white/10 transition-transform disabled:opacity-40";

  const textArea = (className, minHeight) => (
    <textarea
      ref={textRef}
      data-testid="composer-input"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={onKeyDown}
      onCompositionStart={() => { composingRef.current = true; }}
      onCompositionEnd={(e) => {
        composingRef.current = false;
        // Some Android IMEs commit here without firing a final change event.
        setText(e.target.value);
      }}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="sentences"
      spellCheck={false}
      lang="auto"
      enterKeyHint={isTouchDevice() ? "enter" : "send"}
      placeholder={
        native
          ? (pendingFiles.length ? "Caption…" : "Note...")
          : isTouchDevice()
          ? (pendingFiles.length ? "Add a caption (optional)…" : "Paste a link, screenshot or write a note…")
          : pendingFiles.length
          ? "Write something with your file (optional), then press Enter to send…"
          : "Paste a link, screenshot or write a note… (Enter to send, Shift+Enter for newline)"
      }
      rows={1}
      className={className}
      style={{ minHeight }}
      onInput={(e) => {
        e.target.style.height = "auto";
        e.target.style.height = Math.min(e.target.scrollHeight, 160) + "px";
      }}
    />
  );

  return (
    <form onSubmit={submit} className="flex flex-col gap-2">
      {editPhoto && <PhotoEditor file={editPhoto} onCancel={() => setEditPhoto(null)} onSend={sendEditedPhoto} />}
      {pendingFiles.length > 0 && (
        <div data-testid="composer-attachments" className="flex flex-wrap gap-2">
          {pendingFiles.map((f, idx) => (
            <AttachmentChip
              key={`${f.name}-${idx}`}
              file={f}
              index={idx}
              upload={uploads[idx]}
              locked={busy}
              onRemove={() => removeAt(idx)}
            />
          ))}
        </div>
      )}
      {native ? (
        <div data-testid="composer-pill" className="flex items-end gap-0.5 rounded-[1.75rem] bg-[#1E1E1E] border border-[#27272A] p-1.5 focus-within:border-[#3B82F6]/60 transition-colors">
          <button type="button" data-testid="composer-camera-btn" onClick={takePhoto} disabled={busy} aria-label="Take a photo" title="Camera" className={pillIcon}>
            <CameraIcon className="h-[22px] w-[22px]" />
          </button>
          <button type="button" data-testid="composer-gallery-btn" onClick={pickFromGallery} disabled={busy} aria-label="Add photos from gallery" title="Photos" className={pillIcon}>
            <ImageIcon className="h-[22px] w-[22px]" />
          </button>
          <button type="button" data-testid="composer-attach-btn" onClick={() => fileInputRef.current?.click()} disabled={busy} aria-label="Attach file" title="File" className={pillIcon}>
            <Paperclip className="h-[22px] w-[22px]" />
          </button>
          {onDrawLine && (
            <button type="button" data-testid="composer-draw-line-btn" onClick={onDrawLine} disabled={busy} aria-label="Draw a section line" title="Section line" className="h-10 w-9 shrink-0 flex items-center justify-center rounded-full text-[#F87171] active:scale-90 active:bg-white/10 transition-transform disabled:opacity-40">
              <PenLine className="h-[22px] w-[22px]" />
            </button>
          )}
          <input ref={fileInputRef} type="file" multiple data-testid="composer-file-input" className="hidden" onChange={onPickFile} />
          {textArea("flex-1 min-w-0 resize-none bg-transparent px-2 py-2.5 text-[15px] text-[#F3F4F6] placeholder:text-[#9CA3AF] focus:outline-none max-h-40", "40px")}
          <button type="submit" data-testid="send-message-btn" aria-label="Send" disabled={busy || (!text.trim() && !pendingFiles.length)}
            className="h-10 w-10 shrink-0 rounded-full bg-[#2563EB] text-white flex items-center justify-center disabled:opacity-40 active:scale-95 transition-transform">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      ) : (
      <div className="flex items-end gap-1.5 sm:gap-2">
        <button
          type="button"
          data-testid="composer-attach-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          className={btn}
          aria-label="Attach file"
          title="Attach file or photo"
        >
          <Paperclip className="h-4 w-4" />
        </button>
        {onDrawLine && (
          <button type="button" data-testid="composer-draw-line-btn" onClick={onDrawLine} disabled={busy} className={penBtn} aria-label="Draw a section line" title="Draw a section line">
            <PenLine className="h-4 w-4" />
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          data-testid="composer-file-input"
          className="hidden"
          onChange={onPickFile}
        />
        <div className="flex-1 relative">
          {textArea("w-full resize-none bg-[#1E1E1E] border border-[#27272A] rounded-3xl px-5 py-3.5 pr-12 text-sm text-[#F3F4F6] placeholder:text-[#6B7280] focus:outline-none focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6] transition-all max-h-40", "48px")}
        </div>
        <button
          type="submit"
          data-testid="send-message-btn"
          aria-label="Send"
          disabled={busy || (!text.trim() && !pendingFiles.length)}
          className="h-12 w-12 shrink-0 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white flex items-center justify-center disabled:opacity-40 disabled:hover:bg-[#2563EB] transition-all glow-blue"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </div>
      )}
      <DuplicateDialog
        dup={dup}
        onCancel={() => setDup(null)}
        onPasteAgain={() => { setDup(null); submit(null, true); }}
        onFind={() => {
          const { item, title } = dup;
          setDup(null);
          setText("");
          setPendingFiles([]);
          resetTextareaHeight(textRef.current);
          onFindExisting?.(item.item_id, title?.title_id);
        }}
      />
    </form>
  );
});

/** Thumbnail card for a file waiting to be sent, with progress + remove badge. */
function AttachmentChip({ file, index, upload, locked, onRemove }) {
  const isImage = file.type?.startsWith("image/");
  const [url] = useState(() => (isImage ? URL.createObjectURL(file) : null));
  const pct = upload?.pct ?? 0;
  const uploading = !!upload && !upload.done;

  return (
    <div
      data-testid={`composer-attachment-${index}`}
      className="relative group/att bubble-in rounded-xl bg-[#1E1E1E] border border-[#27272A] overflow-hidden"
    >
      {isImage ? (
        <img src={url} alt={file.name} className="h-20 w-20 object-cover bg-[#0A0A0A]" />
      ) : (
        <div className="h-20 w-[9.5rem] px-2.5 flex items-center gap-2">
          <div className="h-10 w-10 shrink-0 rounded-lg bg-[#0A0A0A] border border-[#27272A] flex items-center justify-center">
            <Paperclip className="h-4 w-4 text-[#3B82F6]" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] text-[#F3F4F6] truncate">{file.name}</div>
            <div className="text-[10px] text-[#6B7280]">{(file.size / 1024).toFixed(0)} KB</div>
          </div>
        </div>
      )}
      {isImage && !upload && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-1.5 py-1 flex items-center gap-1">
          <ImageIcon className="h-2.5 w-2.5 text-[#9CA3AF] shrink-0" />
          <span className="text-[9px] text-[#D1D5DB] truncate">{file.name}</span>
        </div>
      )}

      {/* Upload progress — dims the chip and fills a bar along the bottom */}
      {upload && (
        <div
          data-testid={`composer-attachment-progress-${index}`}
          className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-1"
        >
          {upload.done ? (
            <Check className="h-5 w-5 text-[#34D399]" />
          ) : (
            <span className="text-[11px] font-semibold text-[#F3F4F6] tabular-nums">{pct}%</span>
          )}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#27272A]">
            <div
              className="h-full bg-[#3B82F6] transition-[width] duration-200"
              style={{ width: `${upload.done ? 100 : pct}%` }}
            />
          </div>
        </div>
      )}

      {!uploading && !upload?.done && (
        <button
          type="button"
          onClick={onRemove}
          disabled={locked}
          data-testid={`composer-remove-attachment-${index}`}
          aria-label={`Remove ${file.name}`}
          className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/70 border border-[#3F3F46] text-[#E5E7EB] hover:bg-[#EF4444] hover:border-[#EF4444] hover:text-white flex items-center justify-center transition-colors disabled:opacity-40"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

export default Composer;
